import { Icon, addCollection } from '@iconify/react';
import type { SVGProps } from 'react';
import lucideCollection from './lucideIcons.generated.json';

// Register the Lucide glyphs we use as an OFFLINE Iconify collection. Without
// this, @iconify/react fetches each icon from api.iconify.design at runtime —
// a flash of blank on first paint plus a hard dependency on a third-party
// service. lucideIcons.generated.json is a trimmed subset (only the names
// referenced below, ~15 KB). To add an icon: add its export here, then
// regenerate the subset with the icon list — see the curl snippet at the
// bottom of this file.
addCollection(lucideCollection as Parameters<typeof addCollection>[0]);

/**
 * Project icon set — Lucide (via @iconify/react, the `lucide:*` set).
 *
 * Lucide is a single coherent family: 24×24, 2px rounded strokes, no
 * fills. We keep one wrapper (`makeIcon`) so every call-site stays
 * unchanged — sizing is driven by the Tailwind `h-* w-*` classes the
 * call-sites already pass, exactly as before.
 *
 * Most "off" / mute glyphs ship natively in Lucide (mic-off, bell-off,
 * volume-x, phone-off). The few that don't (headphones-off) are
 * composited: the base icon + a diagonal slash overlay drawn as a
 * separate <svg>, using currentColor + matching stroke rounding so the
 * slash reads as part of the glyph, not bolted on.
 */

type IconProps = SVGProps<SVGSVGElement> & {
  /** Optional pixel size override; defaults to 1em (inherits font-size). */
  size?: number | string;
};

function makeIcon(name: string) {
  function ProjectIcon({ className, color, style, size, ...rest }: IconProps) {
    // CRITICAL: only emit width/height in inline style when an explicit
    // `size` prop is passed. Otherwise rely on the className (h-* w-*
    // Tailwind classes) so call-sites stay in control. Setting a default
    // `1em` here would override every h-5 / h-[22px] applied via class
    // (inline styles beat CSS) and silently shrink icons to the body
    // font-size.
    const sizeStyle = size !== undefined
      ? { width: size, height: size, ...style }
      : style;
    return (
      <Icon
        icon={name}
        className={className}
        color={color}
        style={sizeStyle}
        aria-hidden
        focusable={false}
        {...(rest as Record<string, unknown>)}
      />
    );
  }
  ProjectIcon.displayName = `Icon(${name})`;
  return ProjectIcon;
}

// === Lucide mapping ================================================
// Each export keeps its old name + call-signature so nothing else in
// the app changes. Names are the canonical Lucide ids (lucide.dev/icons).

// Close / dismiss — every modal close X / role-chip ✕.
export const CloseIcon = makeIcon('lucide:x');

// Channel + content glyphs
export const HashIcon = makeIcon('lucide:hash');
export function ServerIcon(props: SVGProps<SVGSVGElement>) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable={false} {...props}>
    <rect x="3" y="3" width="7" height="7" rx="2"/>
    <rect x="14" y="3" width="7" height="7" rx="2"/>
    <rect x="3" y="14" width="7" height="7" rx="2"/>
    <rect x="14" y="14" width="7" height="7" rx="2"/>
  </svg>;
}
export const VolumeIcon = makeIcon('lucide:volume-2');
export const ChevronDownIcon = makeIcon('lucide:chevron-down');
export const PinIcon = makeIcon('lucide:pin');
// Bookmark — "Saved Messages" affordance (message context menu + sidebar).
export const BookmarkIcon = makeIcon('lucide:bookmark');
// Saved-state indicator — bookmark with a check so it reads as "saved".
export const BookmarkFilledIcon = makeIcon('lucide:bookmark-check');
// "Сейф" (personal vault chat) — a safe with a dial.
export const VaultIcon = makeIcon('lucide:vault');
export const SearchIcon = makeIcon('lucide:search');
export const SettingsIcon = makeIcon('lucide:settings');
export const PaperclipIcon = makeIcon('lucide:paperclip');
export const SmileIcon = makeIcon('lucide:smile');
export const ReplyIcon = makeIcon('lucide:reply');
export const ForwardIcon = makeIcon('lucide:forward');
// Composer formatting glyphs.
export const BoldIcon = makeIcon('lucide:bold');
export const ItalicIcon = makeIcon('lucide:italic');
export const UnderlineIcon = makeIcon('lucide:underline');
export const StrikethroughIcon = makeIcon('lucide:strikethrough');
export const CodeIcon = makeIcon('lucide:code');
// Spoiler / hide — closed (off) eye; pairs with EyeIcon (open) for preview.
export const SpoilerIcon = makeIcon('lucide:eye-off');
export const QuoteIcon = makeIcon('lucide:quote');
export const LinkIcon = makeIcon('lucide:link');
export const EyeIcon = makeIcon('lucide:eye');
// «Удалить у меня» — hides a message from my view only.
export const EyeOffIcon = SpoilerIcon;
export const EditIcon = makeIcon('lucide:pencil');
export const TrashIcon = makeIcon('lucide:trash-2');
// Circled plus — matches the previous circled add affordance.
export const PlusIcon = makeIcon('lucide:circle-plus');
export const UsersIcon = makeIcon('lucide:users');
export const UserIcon = makeIcon('lucide:user');
export const UserPlusIcon = makeIcon('lucide:user-plus');
export const UserCheckIcon = makeIcon('lucide:user-check');
export const ClockIcon = makeIcon('lucide:clock');
export const ReceiptIcon = makeIcon('lucide:receipt');
export const HandCoinsIcon = makeIcon('lucide:hand-coins');
export const ArrowRightIcon = makeIcon('lucide:arrow-right');
export const ChevronLeftIcon = makeIcon('lucide:chevron-left');
export const MoreHorizontalIcon = makeIcon('lucide:ellipsis');
export const ChatBubbleIcon = makeIcon('lucide:message-circle');
export const CopyIcon = makeIcon('lucide:copy');

// Notifications
export const BellIcon = makeIcon('lucide:bell');
export const BellOffIcon = makeIcon('lucide:bell-off');
export const AtSignIcon = makeIcon('lucide:at-sign');
export const VolumeOffIcon = makeIcon('lucide:volume-x');

// Voice / media
export const MicIcon = makeIcon('lucide:mic');
// Native Lucide off-glyphs — single clean path with an integrated slash
// (full opacity), the same style Discord uses. Reads far cleaner in the small
// circular mute/deafen buttons than the dimmed base-icon + overlay composite.
export const MicOffIcon = makeIcon('lucide:mic-off');
export const HeadphonesIcon = makeIcon('lucide:headphones');
export const HeadphonesOffIcon = makeIcon('lucide:headphone-off');
export const CameraIcon = makeIcon('lucide:video');
// Clean monitor silhouette (user preferred it over a share-arrow variant).
export const ScreenShareIcon = makeIcon('lucide:monitor');

/**
 * «Демонстрация со звуком» — OUR glyph, not a Lucide one, because Lucide has no
 * shape for it and the concept is ours: a screen whose right edge opens into two
 * sound arcs.
 *
 * It marks a share that actually carries system audio. Without it a silent share
 * and one with sound look identical on a tile, and «в демонстрации нет звука»
 * has already been reported as a bug when it was simply a silent share.
 *
 * Drawn on the same 24 grid as Lucide with 2px round strokes, so it sits in a
 * row of stock glyphs without looking pasted in. The arcs are the part that
 * carries the meaning, so they keep full weight; the monitor stays open on the
 * right rather than being a closed box with something added on top.
 */
export function ScreenAudioIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
      focusable={false}
      {...rest}
    >
      <path d="M14 4H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9" />
      <path d="M10 16v4M8 20h5" />
      <path d="M17.8 8.4a4 4 0 0 1 0 5.2" />
      <path d="M20.8 5.8a8 8 0 0 1 0 10.4" />
    </svg>
  );
}
export const PhoneOffIcon = makeIcon('lucide:phone-off');
export const PhoneIcon = makeIcon('lucide:phone');
// Poll glyph — vertical bar chart reads universally as "vote/poll".
export const PollIcon = makeIcon('lucide:bar-chart-2');
// GIF picker — film strip stands in for looping motion content.
export const GifIcon = makeIcon('lucide:film');
// Composer mic — distinct from the voice-channel MicIcon (vocal mic).
export const MicrophoneComposerIcon = makeIcon('lucide:mic-vocal');
export const PlayIcon = makeIcon('lucide:play');
export const PauseIcon = makeIcon('lucide:pause');

// Reorder handle (drag-grip).
export const GripIcon = makeIcon('lucide:grip-vertical');

// Moderation / status
export const CrownIcon = makeIcon('lucide:crown');
// Mod triad: timeout (alarm) / kick (log-out) / ban (shield-ban).
export const AlarmClockIcon = makeIcon('lucide:alarm-clock');
export const LogOutIcon = makeIcon('lucide:log-out');
export const ShieldBanIcon = makeIcon('lucide:shield-ban');
// Reports / moderation ("Пожаловаться").
export const FlagIcon = makeIcon('lucide:flag');

// CS2 / esports flavour
export const TrophyIcon = makeIcon('lucide:trophy');
export const CrosshairIcon = makeIcon('lucide:crosshair');

// Settings-modal navigation glyphs.
export const PaletteIcon = makeIcon('lucide:palette');
export const KeyboardIcon = makeIcon('lucide:keyboard');
export const ShieldIcon = makeIcon('lucide:shield');

// DM folders (Telegram-style) — folder tab + membership checkmark.
export const FolderIcon = makeIcon('lucide:folder');
export const CheckIcon = makeIcon('lucide:check');

// Composer send — an upward arrow (iMessage/ChatGPT style), shown in a filled
// circle while typing. Distinct from Telegram's paper-plane.
export const SendIcon = makeIcon('lucide:arrow-up');

// === Regenerating the offline subset ================================
// lucideIcons.generated.json holds only the glyphs referenced above. After
// adding or removing an export, refresh it from the top of the repo:
//
//   npm run gen:icon-subset
//
// The names come from THIS file, so the subset cannot drift out of sync with
// the code — which is exactly what happened while the list was typed by hand
// into a curl command: five glyphs (vault, clock, flag, user-plus, user-check)
// were missing, and Iconify quietly fetched them from api.iconify.design
// instead. `iconSubset.test.ts` now fails if any name stops resolving.
//
// (HeadphonesOffIcon reuses `headphones` via the slash overlay, so it needs
// no separate entry.)
